

import React, { forwardRef, useImperativeHandle, useRef } from 'react'
import { Box, Button, Grid, IconButton, Menu, Paper, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { trans } from '../../../../_providers/Translation'
import Api from '../../../../_helpers/Api'
import Skeleton from 'react-loading-skeleton'
import NoDataFound from '@/components/partials/NoDataFound'
import Pagination from '@/components/Pagination'
import menuIcon from '@/assets/img/icons/table-menu.png'
import deleteIcon from '@/assets/img/icons/delete.svg'
import DataExportOptions from './DataExportOptions'
import { enqueueSnackbar } from 'notistack'
import Question from '@/components/modals/messages/Question'
import loader from '@/assets/img/loader.svg'
import moment from 'moment'

const DEFAULT_PAGE_SIZE = 10

function formatExportDate(dateString) {
    if (!dateString) return '-'
    const date = moment(dateString)
    if (!date.isValid()) return dateString
    return date.format('DD.MM.YYYY / HH:mm')
}

const DataExportsReady = ({ handleDownload, loadingDataExport }, ref) => {
    const deleteModalRef = useRef(null)

    const [state, setState] = React.useState({
        loading: true,
        items: [],
        total: 0,
        pages: 1,
        page: 1,
        perPage: DEFAULT_PAGE_SIZE,
    })

    useImperativeHandle(ref, () => ({
        refresh: () => {
            fetchAllExports(1, state.perPage)
        }
    }))

    const fetchAllExports = (page = 1, perPage = DEFAULT_PAGE_SIZE) => {
        setState(prev => ({ ...prev, loading: true }))
        Api.get('/data-exports/all', { params: { page, per_page: perPage } })
            .then(res => {
                const data = res.data || {}
                setState(prev => ({
                    ...prev,
                    loading: false,
                    items: Array.isArray(data.items) ? data.items : [],
                    total: data.total || 0,
                    pages: data.pages || 1,
                    page: page,
                    perPage: perPage,
                }))
            })
            .catch(err => {
                setState(prev => ({ ...prev, loading: false, items: [] }))
                console.error('Failed to fetch ready exports:', err)
            })
    }

    React.useEffect(() => {
        fetchAllExports(1, state.perPage)
    }, [])

    const handlePrev = () => {
        if (state.page > 1) fetchAllExports(state.page - 1, state.perPage)
    }
    const handleNext = () => {
        if (state.page < state.pages) fetchAllExports(state.page + 1, state.perPage)
    }

    const handleRefresh = () => {
        fetchAllExports(state.page, state.perPage)
    }

    const handlePage = page => {
        fetchAllExports(page.selected + 1, state.perPage)
    }

    const handleDelete = id => {
        let modal = deleteModalRef.current

        modal.open()

        modal.onSuccess(async () => {
            try {
                await Api.delete(`/data-exports/${id}`)
                handleRefresh()
                enqueueSnackbar(trans('messages.success'), {
                    variant: 'success',
                })
            } catch (err) {
                enqueueSnackbar(trans('messages.deleteError'), {
                    variant: "error",
                })
            }
        })
    }

    return (
        <>
            <Question
                ref={deleteModalRef}
                mainMessage={trans('questions.delete')}
            />
            <div className="row" style={{
                justifyContent: 'space-between',
            }}>

                {loadingDataExport ?
                    <img src={loader} alt="loading" style={{
                        marginLeft: 0,
                    }} />
                    :
                    <button
                        className="button button-primary button-small"
                        onClick={() => handleDownload(state.exportFields, state.selectedRows)}
                        style={{
                            marginBottom: "15px"
                        }}
                    >

                        {trans('buttons.generateExports')}
                    </button>
                }

                <button className="button button-small button-primary" onClick={handleRefresh}>
                    {trans('buttons.refresh')}
                </button>
            </div>
            {state.loading ? (
                <Skeleton height={45} count={5} />
            ) : state.items.length === 0 ? (
                <NoDataFound />
            ) : (
                <table>

                    <thead>
                        <tr>
                            <th>
                                {trans('headings.myExports')}
                            </th>
                            <th className="options">
                                {trans('tables.options')}
                            </th>
                        </tr>
                    </thead>

                    <tbody>
                        {state.items.map((exp, idx) => (
                            <tr key={idx}>
                                <td>
                                    <Box display="flex" flexDirection="column">
                                        <Typography variant="body2">
                                            <b>{trans('labels.exportId') || 'ID'}:</b> {exp.id || '-'}
                                            {exp.user_id && (
                                                <span style={{ marginLeft: 16 }}><b>{trans('labels.user') || 'User ID'}:</b> {exp.user.full_name}</span>
                                            )}
                                            {exp.user_name && (
                                                <span style={{ marginLeft: 16 }}><b>{trans('labels.user') || 'User'}:</b> {exp.user_name}</span>
                                            )}
                                            {exp.status && (
                                                <span style={{ marginLeft: 16 }}><b>{trans('labels.status') || 'Status'}:</b> {trans('exportStatus.' + exp.status) || exp.status}</span>
                                            )}
                                        </Typography>
                                        <Typography variant="body2" style={{ color: '#888', fontSize: 12 }}>
                                            <b>{trans('labels.date') || 'Date'}:</b> {formatExportDate(exp.created_at)}
                                        </Typography>
                                    </Box>
                                </td>
                                <td className="options">
                                    <DataExportOptions
                                        id={exp.id}
                                        handleDelete={handleDelete}
                                        handleDownload={async (id) => {
                                            try {
                                                const response = await Api.get(`/data-exports/download/${id}`, { responseType: 'blob' })
                                                const url = window.URL.createObjectURL(new Blob([response.data]))
                                                const link = document.createElement('a')
                                                link.href = url
                                                link.setAttribute('download', exp.filename || `export-${id}.zip`)
                                                document.body.appendChild(link)
                                                link.click()
                                                link.remove()

                                                enqueueSnackbar(trans('messages.success'), {
                                                    variant: 'success',
                                                })
                                            } catch (err) {
                                                enqueueSnackbar(trans('messages.downloadError'), {
                                                    variant: "error",
                                                })
                                            }
                                        }}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>

                </table>
            )}
            <Pagination
                pages={state.pages}
                page={state.page}
                handlePage={handlePage}
            />
        </>
    )
}

export default forwardRef(DataExportsReady)